import dayjs from "dayjs"

import { invariant, IsoDate } from "#customTypes/types"

import { STANDARD_DATE_FORMAT } from "./dateHelpers"
import {
  getActiveSemesterCalendar,
  indexOfSemester,
  semesterAt,
  semesterForDate,
  startsFor,
} from "./semesterCalendar"

const SEMESTERS = {
  A: "Spring",
  B: "Summer",
  C: "Fall",
} as const

export const formatSemester = (value: string | undefined): string => {
  if (value == null) return ""

  const val = value.split(" ")

  if (val.length !== 2) return "Unknown semester"

  const year = val[0]
  const semester = val[1] as keyof typeof SEMESTERS

  return `${SEMESTERS[semester]} ${year}`
}

const MOCK_NOW = import.meta.env.VITE_MOCK_NOW

// Caution: Moment and DayJS are very forgiving of bad dates
// Passing `true` as the 3rd arg to either enables strict checking
// https://day.js.org/docs/en/parse/string-format
export const getCurrentSemester = (
  dateString: string = dayjs(MOCK_NOW).format(STANDARD_DATE_FORMAT),
  dateFormat: string = STANDARD_DATE_FORMAT
): string => {
  const date = dayjs(dateString, dateFormat, true)

  invariant(date.isValid(), `GetCurrentSemester: Invalid date: ${dateString}`)

  return semesterForDate(getActiveSemesterCalendar(), date)
}

// If we're in the last semester, return the current semester
// TODO: Make this work for all semesters
export const getNextSemester = (
  dateString: string = dayjs(MOCK_NOW).format(STANDARD_DATE_FORMAT),
  dateFormat: string = STANDARD_DATE_FORMAT
): string => {
  const calendar = getActiveSemesterCalendar()
  const currentSemester = getCurrentSemester(dateString, dateFormat)
  const nextSemester = semesterAt(
    calendar,
    indexOfSemester(calendar, currentSemester) + 1
  )

  return nextSemester ?? currentSemester
}

export const addOneSemester = (
  semester: string = getCurrentSemester()
): string | undefined => {
  const calendar = getActiveSemesterCalendar()
  // A missing id is index -1, so the next row is the first semester.
  return semesterAt(calendar, indexOfSemester(calendar, semester) + 1)
}

/**
 * True when `semester` starts after `reference` in the SEMESTER_DATES ladder.
 * Unknown semesters (pre-schema data) are treated as not-future, since the
 * callers use this to exclude semesters that have not started yet.
 */
export const isFutureSemester = (
  semester: string,
  reference: string = getCurrentSemester()
): boolean => {
  const calendar = getActiveSemesterCalendar()
  const semesterIndex = indexOfSemester(calendar, semester)
  const referenceIndex = indexOfSemester(calendar, reference)

  if (semesterIndex === -1 || referenceIndex === -1) return false

  return semesterIndex > referenceIndex
}

export const getSemesterDateRange = (semester: string): [IsoDate, IsoDate] => {
  const calendar = getActiveSemesterCalendar()
  const currentSemesterIndex = indexOfSemester(calendar, semester)

  invariant(
    currentSemesterIndex !== -1,
    `Semester ${semester} not found in the semester calendar`
  )

  const currentSemesterStartDate = startsFor(calendar)[currentSemesterIndex]
  const nextSemesterStartDate =
    calendar.semesters[currentSemesterIndex + 1]?.startDate ?? null

  const startDate = dayjs(currentSemesterStartDate).toISOString()
  // Exclusive end: the next start. The last day of this semester is the day before.
  const endDate = nextSemesterStartDate
    ? dayjs(nextSemesterStartDate).toISOString()
    : dayjs(currentSemesterStartDate).add(6, "month").toISOString()

  return [startDate, endDate]
}

export const getSemesterStartDateIsoString = (
  semester: string = getCurrentSemester()
): IsoDate => {
  const [startDate] = getSemesterDateRange(semester)

  return startDate
}
